import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader,
  Typography, 
  Chip, 
  IconButton, 
  Box,
  Divider,
  useTheme,
  Tooltip,
  Grid,
  useMediaQuery
} from '@mui/material';
import { 
  Business,
  Edit, 
  Delete,
  DeviceThermostat,
  Speed,
  WaterDrop,
  Receipt,
  Link as LinkIcon,
  LocationOn,
  CheckCircle,
  WarningAmber,
  Bolt,
  VpnKey,
  QrCode2
} from '@mui/icons-material';
import { format } from 'date-fns';

const DeviceCard = ({ 
  device, 
  usageData, 
  onEdit, 
  onDelete, 
  onInvite,
  deleteDisabled,
  onSiteClick,
  onClick 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const iconSize = isMobile ? '1.25rem' : '1.5rem';
  const unit = device?.deviceType === 'electrical_meter' ? 'kWh' : 'L';
  const usageIconColor = device?.deviceType === 'electrical_meter' ? theme.palette.warning.main : theme.palette.primary.main;
  const UsageIcon = device?.deviceType === 'electrical_meter' ? Bolt : WaterDrop;
  
  // Check if device needs decryption key
  const needsDecryptKey = device?.deviceType === 'kamstrup_adv' && 
    (!device?.metadata?.decryptKey || device?.metadata?.needsDecryptKey);
  
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (typeof num === 'string') num = parseFloat(num);
    if (isNaN(num)) return '0';
    
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <Card 
      onClick={onClick}
      elevation={1}
      sx={{ 
        mb: 1.5,
        borderRadius: 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[3]
        },
        position: 'relative'
      }}
    >
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} noWrap sx={{ fontWeight: 500, maxWidth: '70%' }}>
            {device.description || "Unnamed Device"}
          </Typography>
          {needsDecryptKey && (
            <Tooltip title="Decryption key required - no readings will be stored until configured">
              <VpnKey 
                sx={{ 
                  fontSize: '1.2rem',
                  color: 'error.main',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 }
                  }
                }} 
              />
            </Tooltip>
          )}
        </Box>
        {(typeof onEdit === 'function' || typeof onDelete === 'function' || typeof onInvite === 'function') && (
          <Box sx={{ display: 'flex' }}>
            {typeof onInvite === 'function' && (
              <Tooltip title="Invite link">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onInvite(device, e);
                  }}
                  sx={{ mr: 0.5 }}
                >
                  <QrCode2 fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {typeof onEdit === 'function' && (
              <Tooltip title="Edit device">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(device, e);
                  }}
                  sx={{ mr: 0.5 }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {typeof onDelete === 'function' && (
              <Tooltip title="Delete device">
                <span>
                  <IconButton 
                    size="small" 
                    disabled={!!deleteDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (deleteDisabled) return;
                      onDelete(device, e);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
      <Divider />
      <CardContent sx={{ p: { xs: 0.75, sm: 1 } }}>
        <Grid container spacing={0.5}>
          {/* First row - Device info */}
          <Grid item xs={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LinkIcon sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  Serial
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  {device.serialNumber}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LocationOn sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  Location
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  {device.location || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {device.isActive ? (
                <CheckCircle sx={{ mr: 0.5, color: 'success.main', fontSize: iconSize }} />
              ) : (
                <WarningAmber sx={{ mr: 0.5, color: 'error.main', fontSize: iconSize }} />
              )}
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  Status
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8rem', color: device.isActive ? 'success.main' : 'error.main' }}>
                  {device.isActive ? "Active" : "Inactive"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {device.sites && device.sites.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              <Grid item xs={4}>
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center",
                    cursor: 'pointer' 
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSiteClick(device.sites[0].id, e);
                  }}
                  title={device.sites.map((s) => s.description).filter(Boolean).join(', ')}
                >
                  <Business sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                      {device.sites.length > 1 ? 'Sites' : 'Site'}
                    </Typography>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8rem', color: 'primary.main' }}>
                      {device.sites.map((s) => s.description).filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {/* Second row - Usage stats */}
          <Grid item xs={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <UsageIcon sx={{ mr: 0.5, color: usageIconColor, fontSize: iconSize }} />
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  Today
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  {usageData?.todayUsage ? `${formatNumber(usageData.todayUsage)} ${unit}` : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <UsageIcon sx={{ mr: 0.5, color: usageIconColor, fontSize: iconSize }} />
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  Yesterday
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  {usageData?.yesterdayUsage ? `${formatNumber(usageData.yesterdayUsage)} ${unit}` : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <UsageIcon sx={{ mr: 0.5, color: usageIconColor, fontSize: iconSize }} />
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem' }}>
                  30-Day
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  {usageData?.last30DaysUsage ? `${formatNumber(usageData.last30DaysUsage)} ${unit}` : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 1,
              pt: 1,
              borderTop: `1px dashed ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  Multiplier:
                </Typography>
                <Chip 
                  label={device.multiplier || 1} 
                  size="small" 
                  variant="outlined" 
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              
              <Typography variant="caption" color="text.secondary">
                {device.updatedAt ? `Updated ${format(new Date(device.updatedAt), 'MMM d, yyyy')}` : ''}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};



export default DeviceCard;
